import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function P2P() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'p2p' | 'arbitrage'>('p2p');
  const [filters, setFilters] = useState({
    type: 'all',
    currency: 'all',
    payment: 'all',
    amount: ''
  });

  const p2pOrders = [
    {
      id: 1,
      user: 'CryptoTrader_Pro',
      type: 'buy',
      amount: 5000,
      price: 0.65,
      currency: 'USD',
      payment: 'Bank Transfer',
      rating: 4.9,
      trades: 234
    },
    {
      id: 2,
      user: 'BlockchainKing',
      type: 'sell',
      amount: 10000,
      price: 0.67,
      currency: 'USD',
      payment: 'PayPal',
      rating: 4.8,
      trades: 189
    },
    {
      id: 3,
      user: 'PepWhale_2024',
      type: 'buy',
      amount: 25000,
      price: 0.64,
      currency: 'EUR',
      payment: 'Bank Transfer',
      rating: 5.0,
      trades: 456
    },
    {
      id: 4,
      user: 'CryptoNinja',
      type: 'sell',
      amount: 7500,
      price: 0.66,
      currency: 'GBP',
      payment: 'Crypto',
      rating: 4.7,
      trades: 123
    }
  ];

  const filteredOrders = p2pOrders.filter(order => {
    if (filters.type !== 'all' && order.type !== filters.type) return false;
    if (filters.currency !== 'all' && order.currency !== filters.currency) return false;
    if (filters.payment !== 'all' && order.payment !== filters.payment) return false;
    if (filters.amount && order.amount < parseFloat(filters.amount)) return false;
    return true;
  });

  const exchangePrices = [
    { name: 'Binance', price: '67,234', change: 2.4, volume: '$24.5B' },
    { name: 'Coinbase', price: '67,450', change: 2.8, volume: '$8.2B' },
    { name: 'Kraken', price: '67,180', change: 2.1, volume: '$2.1B' },
    { name: 'UniSwap', price: '67,520', change: 3.2, volume: '$1.8B' },
    { name: 'PepExchange', price: '0.65', change: 45.2, volume: '$450M' }
  ];

  const arbitrageOpportunities = [
    {
      id: 1,
      pair: 'BTC/USDT',
      buyExchange: 'Binance',
      sellExchange: 'Coinbase',
      buyPrice: 67150,
      sellPrice: 67450,
      profit: 300,
      profitPercent: 0.45,
      volume: '2.5 BTC',
      spread: 0.45,
      risk: 'Low',
      timeLeft: '5m 23s'
    },
    {
      id: 2,
      pair: 'ETH/USDT',
      buyExchange: 'Kraken',
      sellExchange: 'Binance',
      buyPrice: 3380,
      sellPrice: 3420,
      profit: 40,
      profitPercent: 1.18,
      volume: '5.8 ETH',
      spread: 1.18,
      risk: 'Medium',
      timeLeft: '3m 45s'
    },
    {
      id: 3,
      pair: 'PEP/USDT',
      buyExchange: 'PepExchange',
      sellExchange: 'UniSwap',
      buyPrice: 0.645,
      sellPrice: 0.672,
      profit: 0.027,
      profitPercent: 4.19,
      volume: '12.3K PEP',
      spread: 4.19,
      risk: 'High',
      timeLeft: '8m 12s'
    },
    {
      id: 4,
      pair: 'SOL/USDT',
      buyExchange: 'Binance',
      sellExchange: 'Coinbase',
      buyPrice: 98.5,
      sellPrice: 99.8,
      profit: 1.3,
      profitPercent: 1.32,
      volume: '4.2 SOL',
      spread: 1.32,
      risk: 'Low',
      timeLeft: '6m 34s'
    }
  ];

  const exchanges = [
    { name: 'Binance', fee: 0.1, volume: '24.5B', pairs: 350 },
    { name: 'Coinbase', fee: 0.5, volume: '8.2B', pairs: 180 },
    { name: 'Kraken', fee: 0.26, volume: '2.1B', pairs: 120 },
    { name: 'UniSwap', fee: 0.3, volume: '1.8B', pairs: 500 },
    { name: 'PepExchange', fee: 0.05, volume: '450M', pairs: 85 },
  ];

  const handleTradeNow = () => {
    navigate('/p2p');
  };

  const handleExecuteArbitrage = () => {
    navigate('/trading');
  };

  const handleStartP2P = () => {
    navigate('/p2p');
  };

  const handleExploreArbitrage = () => {
    navigate('/trading');
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-blue-100 to-purple-100 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full mb-3 sm:mb-4">
            <i className="ri-exchange-line text-blue-600 text-xs sm:text-sm"></i>
            <span className="text-blue-600 font-semibold text-[10px] sm:text-xs">P2P Trading & Arbitrage</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">
            Trade & Profit from <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Price Differences</span>
          </h2>
          <p className="text-xs sm:text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
            Secure peer-to-peer trading and automated arbitrage opportunities
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-6 sm:mb-8">
          <div className="bg-gray-100 p-0.5 sm:p-1 rounded-lg sm:rounded-xl inline-flex">
            <button
              onClick={() => setActiveTab('p2p')}
              className={`px-4 py-1.5 sm:px-6 sm:py-2 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 text-xs sm:text-sm cursor-pointer whitespace-nowrap ${
                activeTab === 'p2p'
                  ? 'bg-white text-blue-600 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-group-line mr-1.5 sm:mr-2"></i>
              P2P Trading
            </button>
            <button
              onClick={() => setActiveTab('arbitrage')}
              className={`px-4 py-1.5 sm:px-6 sm:py-2 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 text-xs sm:text-sm cursor-pointer whitespace-nowrap ${
                activeTab === 'arbitrage'
                  ? 'bg-white text-purple-600 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-line-chart-line mr-1.5 sm:mr-2"></i>
              Arbitrage
            </button>
          </div>
        </div>

        {activeTab === 'p2p' ? (
          <div className="space-y-6 sm:space-y-8">
            {/* Filter Bar */}
            <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg border border-gray-100">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                <div>
                  <label className="block text-[10px] sm:text-xs font-medium text-gray-700 mb-1 sm:mb-1.5">Type</label>
                  <select
                    value={filters.type}
                    onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-lg text-[10px] sm:text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer pr-8"
                  >
                    <option value="all">All Orders</option>
                    <option value="buy">Buy Orders</option>
                    <option value="sell">Sell Orders</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] sm:text-xs font-medium text-gray-700 mb-1 sm:mb-1.5">Currency</label>
                  <select
                    value={filters.currency}
                    onChange={(e) => setFilters({ ...filters, currency: e.target.value })}
                    className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-lg text-[10px] sm:text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer pr-8"
                  >
                    <option value="all">All Currencies</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] sm:text-xs font-medium text-gray-700 mb-1 sm:mb-1.5">Payment</label>
                  <select
                    value={filters.payment}
                    onChange={(e) => setFilters({ ...filters, payment: e.target.value })}
                    className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-lg text-[10px] sm:text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer pr-8"
                  >
                    <option value="all">All Methods</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="PayPal">PayPal</option>
                    <option value="Crypto">Crypto</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] sm:text-xs font-medium text-gray-700 mb-1 sm:mb-1.5">Amount</label>
                  <input
                    type="number"
                    value={filters.amount}
                    onChange={(e) => setFilters({ ...filters, amount: e.target.value })}
                    placeholder="Min amount"
                    className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-lg text-[10px] sm:text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* P2P Orders */}
            <div className="grid gap-3 sm:gap-4">
              {filteredOrders.map((order) => (
                <div
                  key={order.id}
                  className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                    <div className="flex items-start gap-3 sm:gap-4 flex-1">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-xs sm:text-sm">{order.user.slice(0, 2).toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 text-xs sm:text-sm md:text-base">{order.user}</h3>
                          <span className={`px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full text-[9px] sm:text-[10px] font-medium ${
                            order.type === 'buy'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {order.type.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-gray-600">
                          <span className="flex items-center gap-1">
                            <i className="ri-coin-line"></i>
                            {order.amount.toLocaleString()} PEP
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-money-dollar-circle-line"></i>
                            {order.price} {order.currency}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-bank-card-line"></i>
                            {order.payment}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="text-right">
                        <div className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                          {order.price} <span className="text-xs sm:text-sm text-gray-600">{order.currency}</span>
                        </div>
                        <div className="text-[10px] sm:text-xs text-gray-500">per PEP</div>
                      </div>
                      <button 
                        onClick={handleTradeNow}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 md:px-6 md:py-2.5 rounded-lg sm:rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 text-[10px] sm:text-xs md:text-sm cursor-pointer whitespace-nowrap"
                      >
                        Trade Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-6 sm:space-y-8">
            {/* Arbitrage Opportunities */}
            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
              {arbitrageOpportunities.map((opp) => (
                <div
                  key={opp.id}
                  className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-purple-200"
                >
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">{opp.pair}</h3>
                    <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-bold">
                      +{opp.profitPercent}%
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="bg-blue-50 rounded-lg sm:rounded-xl p-2 sm:p-3">
                      <div className="text-[10px] sm:text-xs text-gray-600 mb-0.5 sm:mb-1">Buy from</div>
                      <div className="font-bold text-blue-600 text-xs sm:text-sm md:text-base">{opp.buyExchange}</div>
                      <div className="text-xs sm:text-sm md:text-base font-semibold text-gray-900 mt-0.5 sm:mt-1">${opp.buyPrice.toLocaleString()}</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg sm:rounded-xl p-2 sm:p-3">
                      <div className="text-[10px] sm:text-xs text-gray-600 mb-0.5 sm:mb-1">Sell to</div>
                      <div className="font-bold text-purple-600 text-xs sm:text-sm md:text-base">{opp.sellExchange}</div>
                      <div className="text-xs sm:text-sm md:text-base font-semibold text-gray-900 mt-0.5 sm:mt-1">${opp.sellPrice.toLocaleString()}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-[10px] sm:text-xs text-gray-600 mb-3 sm:mb-4">
                    <span>Volume: {opp.volume}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {opp.timeLeft}
                    </span>
                  </div>

                  <button 
                    onClick={handleExecuteArbitrage}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 sm:py-2.5 px-4 sm:px-6 rounded-lg sm:rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 text-xs sm:text-sm cursor-pointer whitespace-nowrap"
                  >
                    Execute Arbitrage
                  </button>
                </div>
              ))}
            </div>

            {/* Exchange Comparison */}
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100">
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Live Exchange Prices</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs font-semibold text-gray-600">Exchange</th>
                      <th className="text-right py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs font-semibold text-gray-600">Price</th>
                      <th className="text-right py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs font-semibold text-gray-600">24h Change</th>
                      <th className="text-right py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs font-semibold text-gray-600">Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exchangePrices.map((exchange) => (
                      <tr key={exchange.name} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 sm:py-3 px-2 sm:px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                              <span className="text-white font-bold text-[10px] sm:text-xs">{exchange.name[0]}</span>
                            </div>
                            <span className="font-medium text-gray-900 text-xs sm:text-sm">{exchange.name}</span>
                          </div>
                        </td>
                        <td className="text-right py-2 sm:py-3 px-2 sm:px-4 font-semibold text-gray-900 text-xs sm:text-sm">${exchange.price}</td>
                        <td className="text-right py-2 sm:py-3 px-2 sm:px-4">
                          <span className={`font-semibold text-xs sm:text-sm ${
                            exchange.change >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {exchange.change >= 0 ? '+' : ''}{exchange.change}%
                          </span>
                        </td>
                        <td className="text-right py-2 sm:py-3 px-2 sm:px-4 text-gray-600 text-xs sm:text-sm">{exchange.volume}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-8 sm:mt-12 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative z-10">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-3">
              Ready to Start Trading?
            </h3>
            <p className="text-xs sm:text-sm md:text-base text-white/90 mb-4 sm:mb-6 max-w-2xl mx-auto">
              Join thousands of traders making profits through P2P trading and arbitrage
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button 
                onClick={handleStartP2P}
                className="bg-white text-purple-600 px-6 py-2.5 sm:px-8 sm:py-3 rounded-lg sm:rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 text-xs sm:text-sm md:text-base cursor-pointer whitespace-nowrap"
              >
                Start P2P Trading
              </button>
              <button 
                onClick={handleExploreArbitrage}
                className="bg-white/10 backdrop-blur-sm text-white px-6 py-2.5 sm:px-8 sm:py-3 rounded-lg sm:rounded-xl font-bold hover:bg-white/20 transition-all duration-300 border-2 border-white/30 text-xs sm:text-sm md:text-base cursor-pointer whitespace-nowrap"
              >
                Explore Arbitrage
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}